import type { ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import { cap, clean, escapeHtml, fieldToken, num, shortName, stripHtml, toParagraph, word } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';

const questionsDef: SlotGroupDefinition = {
  label: 'Questions',
  description: 'A question shows only while its answer has data; the contact question always shows.',
  fields: {
    count_q: { label: 'Count · Question', type: 'text', defaultValue: 'How many pets can I have?' },
    weight_q: { label: 'Weight · Question', type: 'text', defaultValue: 'Is there a weight limit?' },
    breeds_q: { label: 'Breeds · Question', type: 'text', defaultValue: 'Are there breed restrictions?' },
    contact_q: { label: 'Contact · Question', type: 'text', defaultValue: 'Who do I ask about my pet?' },
  },
};

const ANSWER =
  'min-w-0 max-w-[680px] text-base leading-[26px] [overflow-wrap:anywhere] [&_a]:font-medium [&_a]:text-[--q-primary-on-paper] [&_a]:underline-offset-[3px] [&_p]:m-0 max-[767px]:text-[15px] max-[767px]:leading-[22px]';

/** An always-open detail row: the minus is the resting mark, not a toggle. */
function Row({ q, children }: { q: ReactNode; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-3 border-t border-t-[--q-border] pb-6 pt-[22px] last:border-b last:border-b-[--q-border] max-[767px]:gap-2 max-[767px]:pb-[18px] max-[767px]:pt-4">
      <div className="flex items-center gap-6 max-[767px]:gap-3">
        <h3 className="m-0 flex-auto font-heading text-[24px] font-normal leading-8 max-[767px]:text-[20px] max-[767px]:leading-[26px]">{q}</h3>
        <span
          className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[--q-border] text-[--q-primary-on-paper] [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-width:1.6]"
          aria-hidden="true"
        >
          <svg width="16" height="16" viewBox="0 0 16 16">
            <path d="M3 8h10" />
          </svg>
        </span>
      </div>
      {children}
    </div>
  );
}

/** "Up to two pets per home. Dogs and cats are both welcome." */
function countAnswer(n: number, dogs: boolean, cats: boolean): string {
  const count = n === 1 ? 'One pet per home.' : `Up to ${word(n)} pets per home.`;
  const kinds = dogs && cats ? 'Dogs and cats are both welcome.' : dogs ? 'Dogs are welcome.' : cats ? 'Cats are welcome.' : '';
  return [count, kinds].filter(Boolean).join(' ');
}

const phoneLink = (field: string) =>
  `<a class="dniphone dniphonehref" data-field-href="${field}" data-field-href-type="tel">${fieldToken(field)}</a>`;

/** How many, how big, which breeds, and who to ask (Paper 13). */
export function PetRules() {
  const p = useRawProperty();
  const editing = useIsEditing();
  const qs = useSlotGroup('petrules.questions', questionsDef);

  const pets = !!p.property_ispetfriendly;
  const maxPets = num(clean(p.property_max_pets));
  const weight = num(clean(p.property_max_pet_weight_limit));
  const breeds = stripHtml(clean(p.property_pet_restrictions) || '');
  const phoneField = clean(p.property_call_tracking_phone) ? 'property_call_tracking_phone' : clean(p.property_phone) ? 'property_phone' : null;
  const name = shortName(clean(p.property_name));

  const showCount = pets && !!maxPets;
  const showWeight = pets && !!weight;
  const showBreeds = pets && !!breeds;
  const showContact = !!phoneField;

  if (!showCount && !showWeight && !showBreeds && !showContact && !editing) return null;

  const team = cap(name ? `the ${escapeHtml(name)} leasing team` : 'the leasing team');

  return (
    <section className="section section--surface tqs flex items-start gap-20 max-[1199px]:flex-col max-[1199px]:gap-8 max-[767px]:gap-7" id="pet-rules">
      <div className="flex flex-[0_0_360px] flex-col gap-4 [&_.h2_p]:m-0 max-[1199px]:basis-auto max-[767px]:gap-3" data-reveal>
        <RichTextContent path="petrules.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="petrules.title" as="h2" className="h2" />
        <RichTextContent
          path="petrules.lead"
          as="p"
          className="m-0 text-base leading-6 text-[--q-muted] [&_p]:m-0 max-[767px]:text-[15px] max-[767px]:leading-[22px]"
        />
      </div>
      <EditableSlotGroup groupPath="petrules.questions" definition={questionsDef} className="min-w-0 max-w-[808px] flex-auto max-[1199px]:w-full max-[1199px]:max-w-none">
        {(showCount || editing) && (
          <Row q={qs.count_q || 'How many pets can I have?'}>
            <RichTextContent
              path="petrules.count_a"
              as="div"
              className={ANSWER}
              defaultValue={maxPets ? toParagraph(countAnswer(maxPets, !!p.property_allows_dogs, !!p.property_allows_cats)) : undefined}
            />
          </Row>
        )}
        {(showWeight || editing) && (
          <Row q={qs.weight_q || 'Is there a weight limit?'}>
            <RichTextContent path="petrules.weight_a" as="div" className={ANSWER} defaultValue={weight ? toParagraph(`${weight} pounds per pet.`) : undefined} />
          </Row>
        )}
        {(showBreeds || editing) && (
          <Row q={qs.breeds_q || 'Are there breed restrictions?'}>
            <RichTextContent path="petrules.breeds_a" as="div" className={ANSWER} />
          </Row>
        )}
        {(showContact || editing) && (
          <Row q={qs.contact_q || 'Who do I ask about my pet?'}>
            <RichTextContent
              path="petrules.contact_a"
              as="div"
              className={ANSWER}
              defaultValue={phoneField ? `<p>${team}, at ${phoneLink(phoneField)}.</p>` : undefined}
            />
          </Row>
        )}
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PetRules',
  displayName: 'Pet Rules',
  tokens: {
    'petrules.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>The fine print</p>' },
    'petrules.title': { label: 'Title', type: 'richtext', defaultValue: '<p>A few rules worth knowing.</p>' },
    'petrules.lead': { label: 'Lead', type: 'richtext', defaultValue: '<p>How many, how big, which breeds, and who to ask.</p>' },
    'petrules.count_a': {
      label: 'Count · Answer',
      description: 'Until edited, gives the maximum number of pets and whether dogs and cats are welcome. Hidden while Max Pets is empty or 0.',
      type: 'richtext',
      defaultValue: '<p>Up to <span data-type="field-token" data-field="property_max_pets"></span> pets per home.</p>',
    },
    'petrules.weight_a': {
      label: 'Weight · Answer',
      description: 'Hidden while Max Pet Weight is empty or 0.',
      type: 'richtext',
      defaultValue: '<p><span data-type="field-token" data-field="property_max_pet_weight_limit"></span> pounds per pet.</p>',
    },
    'petrules.breeds_a': {
      label: 'Breeds · Answer',
      description: 'The pet restrictions as written; hidden while they are empty.',
      type: 'richtext',
      defaultField: 'property_pet_restrictions',
    },
    'petrules.contact_a': {
      label: 'Contact · Answer',
      description: 'Until edited, the call tracking phone (else the property phone) as a call link.',
      type: 'richtext',
      defaultValue:
        '<p>The leasing team, at <a class="dniphone dniphonehref" data-field-href="property_phone" data-field-href-type="tel"><span data-type="field-token" data-field="property_phone"></span></a>.</p>',
    },
  },
  slotGroups: {
    'petrules.questions': questionsDef,
  },
};

(PetRules as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PetRules;
